import React, { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { marked } from 'marked';

const BlogsAndNewsPage = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [educationVideos, setEducationVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const videoSectionRef = useRef(null);

  // Fetch blog and video data from Statamic
  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const response = await fetch('/api/news-and-blogs');
        if (!response.ok) {
          throw new Error('Failed to fetch blog data');
        }
        const data = await response.json();
        
        // Set education videos
        if (data.education_videos && Array.isArray(data.education_videos)) {
          setEducationVideos(data.education_videos);
        }
        
        // Transform Statamic data to match our component structure
        if (data.blogs_news && Array.isArray(data.blogs_news)) {
          const formattedPosts = data.blogs_news
            .filter(post => post.enabled !== false)
            .map(post => ({
              id: post.id,
              title: post.title || post.blog_title,
              excerpt: post.excerpt,
              content: formatContent(post.content),
              author: post.author || 'Eye&U Team',
              date: formatDate(post.posting_date || post.date),
              readTime: post.readTime || post.read_time,
              category: formatCategory(post.category),
              image: post.image || (post.blog_image ? post.blog_image : 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'),
              tags: post.tags ? post.tags.map(tag => tag.replace('tags::', '')) : []
            }));
          
          setBlogPosts(formattedPosts);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching blog data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  // Scroll to video section function
  const scrollToVideos = () => {
    if (videoSectionRef.current) {
      // If we're in the blog detail view, first go back to the main view
      if (selectedBlog) {
        setSelectedBlog(null);
        // Wait for the main view to render, then scroll to videos
        setTimeout(() => {
          videoSectionRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      } else {
        // If we're already in the main view, just scroll to videos
        videoSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // Helper function to format content (markdown to HTML)
  const formatContent = (content) => {
    if (!content) return '';
    
    const htmlContent = marked(content);
    
    return htmlContent
      .replace(/<p>/g, '<p class="mb-4">')
      .replace(/<h3>/g, '<h3 class="text-xl font-bold mt-6 mb-3 text-gray-800">')
      .replace(/<h4>/g, '<h4 class="text-lg font-semibold mt-4 mb-2 text-gray-800">')
      .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4 space-y-2">')
      .replace(/<ol>/g, '<ol class="list-decimal pl-5 mb-4 space-y-2">')
      .replace(/<li>/g, '<li class="mb-1">')
      .replace(/<strong>/g, '<strong class="font-semibold">');
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Helper function to format category
  const formatCategory = (category) => {
    if (!category) return 'Eye Care';
    
    const categoryMap = {
      'eye-conditions': 'Eye Conditions',
      'eye-care-tips': 'Eye Care Tips',
      'nutrition': 'Nutrition',
      'fashion-style': 'Fashion & Style'
    };
    
    return categoryMap[category] || category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Function to get related posts (same category, excluding current post)
  const getRelatedPosts = (currentPost) => {
    return blogPosts
      .filter(post => post.id !== currentPost.id && post.category === currentPost.category)
      .slice(0, 3);
  };

  // Video carousel navigation functions
  const nextVideo = () => {
    setCurrentVideoIndex(prevIndex => 
      prevIndex >= educationVideos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevVideo = () => {
    setCurrentVideoIndex(prevIndex => 
      prevIndex <= 0 ? educationVideos.length - 1 : prevIndex - 1
    );
  };

  const goToVideo = (index) => {
    setCurrentVideoIndex(index);
  };

  // Function to handle video play
  const playVideo = (videoId) => {
    setPlayingVideoId(videoId);
  };

  // Function to stop video
  const stopVideo = () => {
    setPlayingVideoId(null);
  };

  // Function to extract YouTube ID from URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleBookAppointment = () => {
    window.location.href = "/book-appointment-guided-care-journey";
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#08005f] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blogs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center text-red-600">
          <p>Error loading blogs: {error}</p>
          <Button
            className="mt-4 bg-[#08005f] text-white hover:bg-red-600"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // If a blog is selected, show the detailed view
  if (selectedBlog) {
    const relatedPosts = getRelatedPosts(selectedBlog);

    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back button */}
        <button 
          onClick={() => setSelectedBlog(null)}
          className="flex items-center text-[#08005f] hover:text-red-600 mb-6 transition-colors"
        >
          <Icon name="ArrowLeft" size={20} className="mr-2" />
          Back to Blogs
        </button>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main content */}
          <div className="lg:col-span-3">
            {/* Blog content */}
            <article className="bg-white rounded-xl shadow-md overflow-hidden">
              <img 
                src={selectedBlog.image} 
                alt={selectedBlog.title}
                className="w-full h-72 object-cover"
              />
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="bg-[#08005f] text-white text-sm font-medium px-3 py-1 rounded-full">
                    {selectedBlog.category}
                  </span>
                  <span className="text-gray-500 text-sm">{selectedBlog.date}</span>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{selectedBlog.title}</h1>
                
                <div className="flex items-center text-gray-600 mb-6">
                  <div className="flex items-center mr-6">
                    <div className="w-10 h-10 bg-[#08005f] rounded-full flex items-center justify-center text-white mr-3">
                      <Icon name="User" size={18} />
                    </div>
                    <span>By {selectedBlog.author}</span>
                  </div>
                  <span className="flex items-center">
                    <Icon name="Clock" size={16} className="mr-1 text-[#08005f]" />
                    {selectedBlog.readTime}
                  </span>
                </div>
                
                <div 
                  className="prose max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                />
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBlog.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Icon name="BookOpen" size={20} className="mr-2 text-[#08005f]" />
                  Related Posts
                </h3>
                <div className="space-y-4">
                  {relatedPosts.map(post => (
                    <div 
                      key={post.id} 
                      className="cursor-pointer group p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedBlog(post)}
                    >
                      <h4 className="font-medium text-gray-900 group-hover:text-[#08005f] transition-colors line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">{post.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Video Promo */}
<div className="bg-white rounded-xl shadow-md p-6 mt-6">
  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
    <Icon name="Video" size={20} className="mr-2 text-[#08005f]" />
    Watch & Learn
  </h3>
  <p className="text-gray-600 text-sm mb-4">Check out our educational videos on eye health and treatments.</p>
  <Button
    fullWidth
    className="bg-[#08005f] text-white hover:bg-red-600"
    onClick={() => {
      // If we're in a blog post view, first go back to the main view
      if (selectedBlog) {
        setSelectedBlog(null);
        // Wait for the main view to render, then scroll to videos
        setTimeout(() => {
          const videoSection = document.getElementById('video-section');
          if (videoSection) {
            window.scrollTo({
              top: videoSection.offsetTop - 100, // Adjust for any fixed headers
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        // If we're already in the main view, scroll to videos
        const videoSection = document.getElementById('video-section');
        if (videoSection) {
          window.scrollTo({
            top: videoSection.offsetTop - 100, // Adjust for any fixed headers
            behavior: 'smooth'
          });
        }
      }
    }}
  >
    View All Videos
  </Button>
</div>
          </div>
        </div>
      </div>
    );
  }

  // Main blog listing view
  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      {/* Title */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Blogs & News
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Stay informed with the latest eye care news, treatment options, and health tips from our expert ophthalmologists.
        </p>
      </div>

      {/* Blog Posts Grid - 4 columns */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {blogPosts.map((post) => (
          <article 
            key={post.id}
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group border border-gray-100"
          >
            <div className="relative overflow-hidden">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute top-3 left-3">
                <span className="bg-[#08005f] text-white text-xs font-medium px-2 py-1 rounded">
                  {post.category}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-center text-gray-500 text-xs mb-2">
                <span>{post.date}</span>
                <span className="mx-2">•</span>
                <span className="flex items-center">
                  <Icon name="Clock" size={12} className="mr-1" />
                  {post.readTime}
                </span>
              </div>
              
              <h2 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#08005f] transition-colors leading-tight">
                <button onClick={() => setSelectedBlog(post)} className="text-left">
                  {post.title}
                </button>
              </h2>
              
              <p className="text-gray-600 text-xs mb-4 line-clamp-2">{post.excerpt}</p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500 text-xs">
                  <Icon name="User" size={12} className="mr-1" />
                  <span className="truncate">{post.author.split(' ')[0]}</span>
                </div>
                
                <Button
                  size="sm"
                  className="bg-[#08005f] text-white hover:bg-red-600 transition-colors text-xs px-3 py-1"
                  onClick={() => setSelectedBlog(post)}
                >
                  Read More
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Video Section with Carousel */}
<div className="mb-16" id="video-section">
  <div className="text-center mb-8 px-4 sm:px-0">
    <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ color: "#08005f" }}>
      Patient Education Videos
    </h2>
    <p className="text-gray-600 max-w-2xl mx-auto">
      Watch our educational videos to learn more about eye health,
      treatment procedures, and post-care instructions from our expert
      ophthalmologists.
    </p>
  </div>

  {educationVideos.length > 0 && (
    <div className="relative">
      {/* Video Carousel */}
      <div className="overflow-x-auto scrollbar-hide">
        <div 
          className="flex gap-4 transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentVideoIndex * 100}%)` }}
        >
          {educationVideos.map((video, index) => {
            const youtubeId = getYouTubeId(video.url);
            const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : '';
            
            return (
              <div
                key={video.id || index}
                className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/4 px-2"
              >
                <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-gray-100 h-full">
                  <div className="aspect-w-16 aspect-h-9 relative">
                    {playingVideoId === video.id ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                        title={video.title}
                        className="w-full h-full rounded-xl"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div
                        className="w-full h-full relative cursor-pointer"
                        onClick={() => playVideo(video.id)}
                      >
                        <img 
                          src={thumbnailUrl} 
                          alt={video.title}
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition">
                            <Icon name="Play" size={24} className="text-[#08005f] ml-1" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm leading-tight line-clamp-2">
                      {video.title}
                    </h4>
                    <p className="text-gray-600 text-xs mb-2 line-clamp-2">{video.description}</p>
                    <div className="flex items-center text-gray-500 text-xs">
                      <Icon name="Clock" size={12} className="mr-1" />
                      {video.duration}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      {educationVideos.length > 4 && (
        <>
          <button
            onClick={prevVideo}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 sm:-translate-x-4 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors z-10"
            aria-label="Previous videos"
          >
            <Icon name="ArrowLeft" size={20} className="text-[#08005f]" />
          </button>
          <button
            onClick={nextVideo}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 sm:translate-x-4 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors z-10"
            aria-label="Next videos"
          >
            <Icon name="ArrowRight" size={20} className="text-[#08005f]" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {educationVideos.length > 4 && (
        <div className="flex justify-center mt-6 gap-2">
          {Array.from({ length: Math.ceil(educationVideos.length / 4) }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToVideo(index * 4)}
              className={`w-3 h-3 rounded-full ${
                currentVideoIndex >= index * 4 && currentVideoIndex < (index + 1) * 4
                  ? 'bg-[#08005f]'
                  : 'bg-gray-300'
              }`}
              aria-label={`Go to video set ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )}
</div>


      {/* Call to Action */}
      <div className="bg-gradient-to-r from-[#08005f] to-[#08005f] text-white rounded-xl shadow-lg p-8 text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">
            Need Professional Eye Care Advice?
          </h2>
          <p className="text-lg mb-6">
            Our expert team is here to provide personalized eye care solutions.
            Book your appointment today for comprehensive vision care.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#08005f] hover:bg-gray-100 font-medium"
            onClick={handleBookAppointment}
          >
            Book Your Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogsAndNewsPage;